// ** React Imports
import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";

// ** MUI Components
import { Box, Typography, Tooltip, IconButton, Popover } from "@mui/material";

// ** Icon Imports
import Icon from "components/icon";

// ** Custom Component Imports
import CustomChip from "components/mui/chip";

// ** Third-Party Imports
import dayjs from "dayjs";
import { ToastContainer, toast } from "react-toastify";

// ** Logic Imports
import { GetAllFilteredJobs } from "screens/WorkOrders/logic/GetAllJobs";
import { GetWorkReqService } from "screens/Requests/logic/GetWorkRequirement";
import { useWorkRequirementLogic } from "screens/WorkOrders/logic/work-requirement-logic";
import { GetServiceRequests } from "screens/Requests/logic/GetServiceRequest";

export const useJobListLogic = (selectedDate, setLoading) => {
  //#region Hooks
  const navigate = useNavigate();
  //#endregion Hooks

  //#region States
  const [jobList, setJobList] = useState([]);
  const [jobListPaginationModel, setJobListPaginationModel] = useState({
    page: 0,
    pageSize: 10,
  });
  const [openJobDetail, setOpenJobDetail] = useState(false);
  const [selectedWorkReq, setSelectedWorkReq] = useState(null);
  const [selectedJob, setSelectedJob] = useState(null);
  const [serviceRequest, setServiceRequest] = useState({});
  const [rules, setRules] = useState({
    canEdit: true,
    canDelete: true,
    canSubmit: true,
    nextStatuses: [],
    cannotGoBack: false,
  });
  let payload = {
    workReqID: 0,
    requestID: 0,
    startDate: dayjs(selectedDate).format("YYYY-MM-DD"),
  };
  //#endregion States

  //#region Props Destructuring
  const { WorkRequirementsProps } = useWorkRequirementLogic({
    requestID: 0,
    navigate,
    toast,
    setLoading,
    serviceRequest,
  });

  const { fetchWorkRequirements } = WorkRequirementsProps;
  //#endregion Props Destructuring

  //#region Handlers
  const fetchJobs = async () => {
    try {
      const result = await GetAllFilteredJobs(payload, navigate);
      if (result) {
        const formattedData = result.map((job, index) => ({
          ...job,
          id: job.jobID,
        }));
        setJobList(formattedData);
      }
    } catch (error) {
      console.error("Error fetching jobs:", error);
    }
  };

  useEffect(() => {
    if (selectedDate) {
      fetchJobs();
    }
  }, [selectedDate]);

  const handleOpenJobDetail = async (
    requestID,
    workReqID,
    jobID,
    customerID
  ) => {
    const serviceReq = await GetServiceRequests(customerID, requestID, 0);
    const workRequirementList = await GetWorkReqService(requestID, 0, navigate);

    const workReq = workRequirementList?.find(
      (wr) => wr.workReqID === workReqID
    );
    const job = workReq?.jobs.find((j) => j.jobID === jobID);

    setServiceRequest(serviceReq);
    setSelectedWorkReq(workReq);
    setSelectedJob(job);
    setOpenJobDetail(true);
    fetchJobs();
  };

  const handleCloseJobDetail = () => {
    setOpenJobDetail(false);
    setSelectedWorkReq(null);
    setSelectedJob(null);
  };
  //#endregion Handlers

  //#region Styled Components
  const dropdownItemsStyle = {
    px: 6,
    py: 2,
    "&:hover": {
      backgroundColor: "action.hover",
      cursor: "pointer",
    },
  };

  const OptionsButton = () => {
    const [anchorEl, setAnchorEl] = useState(null);

    const handleClick = (event) => {
      setAnchorEl(event.currentTarget);
    };

    const handleClose = () => {
      setAnchorEl(null);
    };

    const open = Boolean(anchorEl);
    const id = open ? "simple-popover" : undefined;

    return (
      <>
        <IconButton onClick={handleClick}>
          <Icon icon="tabler:dots-vertical" />
        </IconButton>
        <Popover
          id={id}
          open={open}
          anchorEl={anchorEl}
          onClose={handleClose}
          anchorOrigin={{
            vertical: "bottom",
            horizontal: "right",
          }}
          transformOrigin={{
            vertical: "top",
            horizontal: "right",
          }}
        >
          <Box sx={{ py: 2 }}>
            <Box
              sx={{
                display: "flex",
                flexDirection: "column",
              }}
            >
              <Typography variant="h6" sx={dropdownItemsStyle}>
                Add Asset
              </Typography>
              <Typography variant="h6" sx={dropdownItemsStyle}>
                Add Labor
              </Typography>
            </Box>
          </Box>
        </Popover>
      </>
    );
  };
  //#endregion Styled Components

  //#region Column Configuration
  const columnsJobList = [
    {
      flex: 0.1,
      field: "jobID",
      minWidth: 120,
      headerName: "Job ID",
      renderCell: ({ row }) => {
        const { requestID, workReqID, jobID, customerID } = row;

        return (
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              height: "100%",
            }}
          >
            <Typography
              sx={{ 
                flexGrow: 1,
                cursor: 'pointer',
                '&:hover': {
                  textDecoration: 'underline'
                }
              }}
              onClick={() => handleOpenJobDetail(requestID, workReqID, jobID, customerID)}
            >
              {jobID}
            </Typography>
          </Box>
        );
      },
    },
    {
      flex: 0.1,
      field: "jobsiteAddress",
      minWidth: 140,
      headerName: "Jobsite",
      renderCell: ({ row }) => {
        const { jobsiteAddress, jobsiteName } = row;

        return (
          <Box
            sx={{
              display: "flex",
              flexDirection: "column",
              justifyContent: "center",
              height: "100%",
            }}
          >
            <Typography
              noWrap
              variant="body2"
              sx={{ color: "text.primary", fontWeight: 600 }}
            >
              {jobsiteName || ""}
            </Typography>
            <Typography noWrap color="text.secondary" fontSize="small">
              {jobsiteAddress || ""}
            </Typography>
          </Box>
        );
      },
    },
    {
      flex: 0.1,
      field: "customerName",
      headerName: "Customer Name",
      minWidth: 180,
      renderCell: ({ row }) => {
        const { customerName, customerID } = row;

        return (
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              height: "100%",
              cursor: "pointer",
              "&:hover": {
                textDecoration: "underline",
              },
            }}
            onClick={() => navigate(`/customers/detail/${customerID}`)}
          >
            <Typography sx={{ flexGrow: 1 }}>{customerName || "-"}</Typography>
          </Box>
        );
      },
    },
    {
      flex: 0.1,
      field: "service",
      minWidth: 140,
      headerName: "Service",
      renderCell: ({ row }) => {
        const { service } = row;

        return (
          <Box sx={{ display: "flex", alignItems: "center", height: "100%" }}>
            <Typography sx={{ flexGrow: 1 }}>{service || "-"}</Typography>
          </Box>
        );
      },
    },
    {
      flex: 0.1,
      field: "scheduleStartTime",
      headerName: "Scheduled Start",
      minWidth: 180,
      renderCell: ({ row }) => {
        const { scheduleStartTime } = row;

        return (
          <Box sx={{ display: "flex", alignItems: "center", height: "100%" }}>
            <Typography sx={{ flexGrow: 1 }}>
              {scheduleStartTime
                ? dayjs(`2000-01-01T${scheduleStartTime}`).format("hh:mm A")
                : "-"}
            </Typography>
          </Box>
        );
      },
    },
    {
      flex: 0.1,
      field: "scheduleEndTime",
      headerName: "Scheduled End",
      minWidth: 180,
      renderCell: ({ row }) => {
        const { scheduleEndTime } = row;

        return (
          <Box sx={{ display: "flex", alignItems: "center", height: "100%" }}>
            <Typography sx={{ flexGrow: 1 }}>
              {scheduleEndTime
                ? dayjs(`2000-01-01T${scheduleEndTime}`).format("hh:mm A")
                : "-"}
            </Typography>
          </Box>
        );
      },
    },
    {
      flex: 0.1,
      field: "actualStart",
      headerName: "Actual Start",
      minWidth: 180,
      renderCell: ({ row }) => {
        const { actualStart } = row;

        return (
          <Box sx={{ display: "flex", alignItems: "center", height: "100%" }}>
            <Typography sx={{ flexGrow: 1 }}>
              {actualStart ? dayjs(actualStart).format("hh:mm A") : "-"}
            </Typography>
          </Box>
        );
      },
    },
    {
      flex: 0.1,
      field: "actualEnd",
      headerName: "Actual End",
      minWidth: 180,
      renderCell: ({ row }) => {
        const { actualEnd } = row;

        return (
          <Box sx={{ display: "flex", alignItems: "center", height: "100%" }}>
            <Typography sx={{ flexGrow: 1 }}>
              {actualEnd ? dayjs(actualEnd).format("hh:mm A") : "-"}
            </Typography>
          </Box>
        );
      },
    },
    {
      flex: 0.1,
      field: "actualDuration",
      headerName: "Actual Duration",
      minWidth: 180,
      renderCell: ({ row }) => {
        const { actualDuration } = row;

        return (
          <Box sx={{ display: "flex", alignItems: "center", height: "100%" }}>
            <Typography sx={{ flexGrow: 1 }}>
              {actualDuration ? `${actualDuration} Hrs` : "-"}
            </Typography>
          </Box>
        );
      },
    },
    {
      flex: 0.1,
      field: "status",
      headerName: "Status",
      minWidth: 150,
      renderCell: ({ row }) => {
        const { status } = row;

        return (
          <CustomChip
            rounded
            size="small"
            skin="light"
            sx={{ lineHeight: 1 }}
            color={
              status === "Not Viewed" || status === "Under Review"
                ? "info"
                : status === "Completed" || status === "Submitted"
                ? "success"
                : status === "On Hold" || status === "In Progress"
                ? "warning"
                : status === "Canceled" || status === "Closed"
                ? "error"
                : "secondary"
            }
            label={status}
          />
        );
      },
    },
    {
      flex: 0.1,
      field: "flags",
      headerName: "Flags",
      minWidth: 150,
      renderCell: (params) => (
        <Box
          sx={{ display: "flex", alignItems: "center", height: "100%", gap: 1 }}
        >
          {params.row.flags && params.row.flags?.length > 0
            ? params.row.flags.map((flag, index) => (
                <Tooltip
                  key={index}
                  title={flag.flagName}
                  disableInteractive
                  placement='left'
                  sx={{ zIndex: 10000 }}
                  PopperProps={{
                    disablePortal: true,
                    modifiers: [
                      {
                        name: "preventOverflow",
                        options: {
                          boundary: "clippingParents",
                        },
                      },
                    ],
                  }}
                >
                  <Box sx={{ display: "flex", alignItems: "center" }}>
                    <Icon
                      icon={
                        flag.flagCategory === "Budget"
                          ? "tabler:currency-dollar"
                          : "tabler:clock-exclamation"
                      }
                      color="red"
                      sx={{ cursor: "pointer" }}
                    />
                  </Box>
                </Tooltip>
              ))
            : null}
        </Box>
      ),
    }
  ];
  //#endregion Column Configuration

  const JobListProps = {
    jobList,
    columnsJobList,
    jobListPaginationModel,
    setJobListPaginationModel,
    openJobDetail,
    handleCloseJobDetail,
    selectedWorkReq,
    selectedJob,
    setSelectedJob,
    fetchWorkRequirements,
    rules,
    fetchJobs
  };

  return {
    JobListProps,
  };
};
